import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { map, filter, mergeMap, catchError } from 'rxjs/operators';

import { getChartHistory } from 'actions/trade';
import { MINI_CHART } from 'actionTypes/miniChart';
import {
  SUBSCRIBE_OHLC_CANDLE_MINI_CHART,
  UNSUBSCRIBE_OHLC_CANDLE_MINI_CHART,
  MINI_CHART_REALTIME_BAR,
} from 'actionTypes/socket';
import TRADE from 'actionTypes/trade';
import { computeTimeRange, getSubscriptionValue } from 'components/chart/helper';
import { useMiniChartStoreBase } from 'stores/miniChart/miniChart.store';
import { CustomAction } from 'types/Istore';
import { ChartRealtimePayload } from 'types/TradingViewSubscription';

import { payloadGenerator, wsSubject } from './socket';

const ohlcCandleChannelSubscribe = (symbol: string, resolution: string) => {
  let typeValue;

  if (resolution) {
    if (resolution.indexOf('H') > -1) {
      typeValue = resolution.replace(/H/g, 'h');
    } else if (resolution.indexOf('W') > -1) {
      typeValue = resolution.replace(/W/g, 'w');
    } else if (resolution.indexOf('D') > -1) {
      typeValue = resolution.replace(/D/g, 'd');
    } else {
      typeValue = resolution;
    }
  }
  const subscribeType = `candlestick_${typeValue}`;
  return wsSubject.multiplex(
    () => payloadGenerator('subscribe', subscribeType, [symbol]),
    () => payloadGenerator('unsubscribe', subscribeType, [symbol]),
    message => message.type === subscribeType
  );
};

const unsubscribeOHLCCandle = (symbol, type) => {
  const typeValue = type.replace(/H/g, 'h');
  const subscribeType = `candlestick_${typeValue}`;
  return wsSubject.multiplex(
    () => payloadGenerator('unsubscribe', subscribeType, [symbol]),
    () => {},
    message => message.type === subscribeType
  );
};

export const subscribeMiniOHLCEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(SUBSCRIBE_OHLC_CANDLE_MINI_CHART),
    mergeMap(action => {
      const { symbol, resolution } = action.payload;
      const socketResolution = getSubscriptionValue(resolution);
      return ohlcCandleChannelSubscribe(symbol, socketResolution).pipe(
        map((msg: ChartRealtimePayload) => {
          const tSec = Math.floor(Number(msg.candle_start_time) / 1_000_000);
          return {
            type: MINI_CHART_REALTIME_BAR,
            payload: {
              symbol: msg.symbol,
              resolution: msg.resolution || resolution,
              bar: {
                time: tSec,
                open: Number(msg.open) ?? Number(msg.close),
                high: Number(msg.high) ?? Number(msg.close),
                low: Number(msg.low) ?? Number(msg.close),
                close: Number(msg.close),
                volume: msg.volume ?? null,
              },
            },
          };
        }),
        catchError(() => EMPTY)
      );
    })
  );

export const unsubscribeMiniOHLCEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_OHLC_CANDLE_MINI_CHART),
    mergeMap(action => {
      const { symbol, resolution } = action.payload;
      const socketResolution = getSubscriptionValue(resolution);
      return unsubscribeOHLCCandle(symbol, socketResolution).pipe(
        catchError(() => EMPTY)
      );
    })
  );

const isSecondsResolution = (r: string) => r.endsWith('S');
const normalizeApiResolution = (r: string) => {
  const resolutionMap: Record<string, string> = {
    '1m': '1',
    '3m': '3',
    '5m': '5',
    '15m': '15',
    '30m': '30',
    '1h': '60',
    '2h': '120',
    '4h': '240',
    '6h': '360',
    '1d': 'D',
    '1D': 'D',
    D: 'D',
    '1w': '1W',
    '2w': '2W',
    '30d': '30D',
  };
  return resolutionMap[r] ?? r;
};

export const requestHistoryEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(MINI_CHART.REQUEST_HISTORY),
    mergeMap((action: any) => {
      const { symbol, resolution, history } = useMiniChartStoreBase.getState().state;
      if (!symbol) return EMPTY;
      const older = !!action.payload?.older;
      const isSec = isSecondsResolution(resolution);
      const apiResolution = normalizeApiResolution(resolution);
      const nowSec = Math.floor(Date.now() / 1000);
      const earliest = history?.t?.length ? history.t[0] : undefined;
      const periodTo = older && earliest ? Math.max(0, earliest - 1) : nowSec;
      const { fromTime, toTime } = computeTimeRange({
        isSeconds: isSec,
        resolution,
        apiResolution,
        periodTo,
        countBack: 1000,
      });

      // Return an Observable of the action to dispatch
      return of(
        getChartHistory({
          symbol,
          type: 'miniChart',
          resolution: apiResolution,
          from: fromTime,
          to: toTime,
          older, // <-- pass the flag
        })
      );
    })
  );

export const miniChartHistoryEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(TRADE.CHART_HISTORY.SUCCESS),
    filter((action: any) => action.params?.type === 'miniChart' && action.result?.result),
    map((action: any) => {
      const store = useMiniChartStoreBase.getState();
      const { history, resolution } = store.state;
      const { setHistory, prependHistory, setHasMore } = store.actions;
      const payload = action.result.result; // { t,o,h,l,c,v }
      const isOlderRequest = !!action.params?.older;

      if (isOlderRequest && (!payload.t || payload.t.length === 0)) {
        setHasMore(false);
        return { type: MINI_CHART.HISTORY_SUCCESS };
      }
      if (!history || !history.t?.length) {
        setHistory({
          symbol: action.params.symbol,
          resolution: String(resolution),
          result: payload,
        });
      } else {
        const earliest = history.t[0];
        const newMax = payload.t[payload.t.length - 1];
        const isOlder = newMax <= earliest;
        if (isOlder && payload.t.length > 0) {
          prependHistory({ result: payload });
        } else {
          setHistory({
            symbol: action.params.symbol,
            resolution: String(resolution),
            result: payload,
          });
        }
      }
      return { type: MINI_CHART.HISTORY_SUCCESS };
    })
  );

export const miniChartHistoryFailureEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(TRADE.CHART_HISTORY.FAIL),
    filter((action: any) => action.params?.type === 'miniChart'),
    map(() => {
      const { setHistory } = useMiniChartStoreBase.getState().actions;
      setHistory({
        symbol: '',
        resolution: '',
        result: { t: [], o: [], h: [], l: [], c: [], v: [] },
      });
      return { type: MINI_CHART.HISTORY_FAILURE };
    })
  );

export const miniChartRealtimeEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(MINI_CHART_REALTIME_BAR),
    map((action: any) => {
      const { updateLastBar } = useMiniChartStoreBase.getState().actions;
      updateLastBar(
        action.payload.symbol,
        String(action.payload.resolution),
        action.payload.bar
      );
      return { type: '@@miniChart/lastBarUpdated' }; // no-op Redux event
    })
  );

export default combineEpics(
  subscribeMiniOHLCEpic,
  unsubscribeMiniOHLCEpic,
  requestHistoryEpic,
  miniChartHistoryEpic,
  miniChartHistoryFailureEpic,
  miniChartRealtimeEpic
);
